import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/useEntity';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { fmt } from '../../lib/formatters';
import { toast } from '../../lib/toast';

export type PedidoAFusionar = {
  id: string;
  fecha_pedido: string;
  numero_approx: string | null;
  numero_cliente: string | null;
  estado: string;
  estado_nombre: string | null;
  estado_color: string | null;
  lineas_count: number;
  unidades_total: number;
  subtotal_eur: number | string | null;
};

const ERRORES: Record<string, string> = {
  pocos_pedidos: 'Hacen falta al menos dos pedidos.',
  clientes_distintos: 'Solo se pueden fusionar pedidos del mismo cliente.',
  pedido_no_encontrado: 'Alguno de los pedidos ya no existe o se ha borrado. Recarga la lista.',
  destino_no_incluido: 'El pedido que se queda tiene que estar entre los seleccionados.',
};

/**
 * «Fusionar pedidos» (CEO, 26/10/2026): los pedidos seleccionados quedan en
 * uno solo con todas sus líneas. Se elige cuál se queda (por defecto el más
 * antiguo): conserva su número, estado y fechas de cabecera; los demás se
 * borran en suave y su nota dice dónde acabaron (POST /pedidos-cliente/fusionar).
 */
export function FusionarPedidosModal({ pedidos, onClose, onDone }: {
  pedidos: PedidoAFusionar[];
  onClose: () => void;
  onDone: () => void;
}) {
  const qc = useQueryClient();
  const ordenados = [...pedidos].sort((a, b) => a.fecha_pedido.localeCompare(b.fecha_pedido));
  const [destinoId, setDestinoId] = useState(ordenados[0]?.id ?? '');
  const numero = (p: PedidoAFusionar) => p.numero_cliente ?? p.numero_approx ?? fmt.date(p.fecha_pedido);
  const destino = ordenados.find((p) => p.id === destinoId);
  const estados = new Set(ordenados.map((p) => p.estado));
  const lineas = ordenados.reduce((a, p) => a + Number(p.lineas_count ?? 0), 0);
  const unidades = ordenados.reduce((a, p) => a + Number(p.unidades_total ?? 0), 0);
  const importe = ordenados.reduce((a, p) => a + Number(p.subtotal_eur ?? 0), 0);

  const fusionar = useMutation({
    mutationFn: () => api.post<{ pedidos_fusionados: number; lineas_movidas: number; estado: { estado_nuevo: string } | null }>(
      '/pedidos-cliente/fusionar',
      { ids: ordenados.map((p) => p.id), destino_id: destinoId },
    ),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['pedidos-cliente'] });
      qc.invalidateQueries({ queryKey: ['pedidos-cliente-lineas'] });
      qc.invalidateQueries({ queryKey: ['pedido-cliente'] });
      const estado = r.estado?.estado_nuevo;
      toast.success(
        `Pedidos fusionados en ${destino ? numero(destino) : 'uno'}`,
        `${fmt.int(r.lineas_movidas)} línea(s) de ${fmt.int(r.pedidos_fusionados)} pedido(s) pasan al pedido que se queda.${estado ? ` Estado recalculado: ${estado}.` : ''}`,
      );
      onDone();
    },
    onError: (err) => {
      const msg = (err as Error).message;
      const codigo = Object.keys(ERRORES).find((c) => msg.includes(c));
      toast.error('No se fusionaron los pedidos', codigo ? ERRORES[codigo] : msg);
    },
  });

  return (
    <Modal
      open
      onClose={onClose}
      title={`Fusionar ${pedidos.length} pedidos`}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={fusionar.isPending}>Cancelar</Button>
          <Button variant="save" onClick={() => fusionar.mutate()} disabled={!destino || pedidos.length < 2 || fusionar.isPending}>
            {fusionar.isPending ? 'Fusionando…' : `🔗 Fusionar en ${destino ? numero(destino) : '…'}`}
          </Button>
        </>
      }
    >
      <div className="space-y-3 text-sm">
        <p className="text-slate-600">
          Elige el pedido que se queda. Conserva su número, estado y fechas de cabecera y recibe las líneas de los demás
          (con sus entregas, fechas e historial). Los otros pedidos se borran y su nota dice en cuál se fusionaron.
        </p>
        <div className="overflow-auto rounded-md border border-slate-200">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-600">
              <tr>
                <th className="px-2 py-1.5 text-left">Se queda</th>
                <th className="px-2 py-1.5 text-left">Pedido</th>
                <th className="px-2 py-1.5 text-left">Fecha</th>
                <th className="px-2 py-1.5 text-left">Estado</th>
                <th className="px-2 py-1.5 text-right">Líneas</th>
                <th className="px-2 py-1.5 text-right">Uds</th>
                <th className="px-2 py-1.5 text-right">Importe</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {ordenados.map((p) => (
                <tr key={p.id} className={p.id === destinoId ? 'bg-emerald-50' : ''}>
                  <td className="px-2 py-1.5">
                    <input
                      type="radio" name="destino" checked={p.id === destinoId}
                      onChange={() => setDestinoId(p.id)}
                      aria-label={`Quedarse con el pedido ${numero(p)}`}
                      className="cursor-pointer"
                    />
                  </td>
                  <td className="px-2 py-1.5 font-mono text-xs">{numero(p)}</td>
                  <td className="px-2 py-1.5 text-xs">{fmt.date(p.fecha_pedido)}</td>
                  <td className="px-2 py-1.5"><Badge color={p.estado_color ?? '#64748b'}>{p.estado_nombre ?? p.estado}</Badge></td>
                  <td className="px-2 py-1.5 text-right font-mono text-xs">{fmt.int(p.lineas_count)}</td>
                  <td className="px-2 py-1.5 text-right font-mono text-xs">{fmt.int(p.unidades_total)}</td>
                  <td className="px-2 py-1.5 text-right font-mono text-xs">{fmt.eur(Number(p.subtotal_eur ?? 0))}</td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-slate-50 text-xs font-semibold">
              <tr>
                <td className="px-2 py-1.5" colSpan={4}>Pedido resultante</td>
                <td className="px-2 py-1.5 text-right font-mono">{fmt.int(lineas)}</td>
                <td className="px-2 py-1.5 text-right font-mono">{fmt.int(unidades)}</td>
                <td className="px-2 py-1.5 text-right font-mono">{fmt.eur(importe)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
        {estados.size > 1 && (
          <p className="rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900">
            Los pedidos están en estados distintos. El resultante queda en el estado del que se queda
            ({destino?.estado_nombre ?? destino?.estado}), recalculado por entregas si hace falta.
          </p>
        )}
        <p className="text-xs text-slate-500">
          La fecha del pedido que se queda es la que cuenta para el descuento técnico, la comisión y el rappel de todas las líneas.
        </p>
      </div>
    </Modal>
  );
}
